import { useTranslation } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { FilterBarRow } from "@impero-com/ui";

import { FilterBarDateTimeRange } from "components/table/FilterBarDateTimeRange/FilterBarDateTimeRange";
import { FilterBarPeriodTimeRange } from "components/table/FilterBarPeriodTimeRange/FilterBarPeriodTimeRange";

import {
    ActiveDateFilter,
    ControlActivitiesDateFilters,
} from "./useControlActivitiesDateFilters";

interface ControlActivitiesDateFilterRowProps {
    dateFilters: ControlActivitiesDateFilters;
    /** Shown when the filter itself sets no bounds — an unbounded filter is displayed as the period it resolves to. */
    dueDateBounds: [string, string] | null;
}

export function ControlActivitiesDateFilterRow({
    dateFilters,
    dueDateBounds,
}: ControlActivitiesDateFilterRowProps) {
    const { activeFilter, setActiveFilter } = dateFilters;
    const { t } = useTranslation();

    const optionLabels: Record<ActiveDateFilter, string> = {
        dueDate: t("ControlResult_DueDate"),
        period: t("ControlActivitiesList_CoveredPeriodRangeFilter"),
    };

    const columnOptions = (Object.keys(optionLabels) as ActiveDateFilter[]).map(
        (value) => ({ value, label: optionLabels[value] })
    );

    const isActiveDateFilter = (value: string): value is ActiveDateFilter =>
        value in optionLabels;

    const handleActiveFilterChange = (value: string) => {
        if (isActiveDateFilter(value)) {
            setActiveFilter(value);
        }
    };

    const renderRangePicker = () => {
        switch (dateFilters.activeFilter) {
            case "dueDate": {
                const hasBound = Boolean(
                    dateFilters.dueDateRange?.[0] ||
                    dateFilters.dueDateRange?.[1]
                );
                return (
                    <FilterBarDateTimeRange
                        filterValue={
                            hasBound ? dateFilters.dueDateRange : dueDateBounds
                        }
                        setFilterValue={dateFilters.setDueDateRange}
                        allowEmpty={[false, false]}
                    />
                );
            }
            case "period":
                return (
                    <FilterBarPeriodTimeRange
                        filterValue={dateFilters.periodRange}
                        setFilterValue={dateFilters.setPeriodRange}
                        allowEmpty={[false, false]}
                    />
                );
            default:
                return assertNever(dateFilters);
        }
    };

    return (
        <FilterBarRow
            columnOptions={columnOptions}
            selectedColumnOption={activeFilter}
            onColumnOptionChange={handleActiveFilterChange}
        >
            {renderRangePicker()}
        </FilterBarRow>
    );
}
